import { Box, Typography, Chip, Card, CardContent } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Pedido, StatusPedido } from '../../types/pedido';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PedidoCardProps {
  pedido: Pedido;
  index: number;
  onClick: () => void;
  onStatusUpdated?: () => void;
}

const getStatusColor = (status: StatusPedido) => {
  // Cores personalizadas para combinar com o StatusColumn
  const colors = {
    [StatusPedido.RECEBIDO]: 'default', // Preto
    [StatusPedido.EM_PREPARO]: 'primary', // Azul escuro
    [StatusPedido.EM_ROTA]: 'warning', // Laranja escuro
    [StatusPedido.PRONTO]: 'success', // Verde escuro
    [StatusPedido.ENTREGUE]: 'success', // Verde escuro
    [StatusPedido.CANCELADO]: 'error' // Vermelho para cancelados
  };
  return colors[status] || 'default';
};

export const PedidoCard = ({ pedido, index, onClick }: PedidoCardProps) => {
  // Validar se o pedido tem todas as propriedades necessárias
  const safePedido: Pedido = {
    id: String(pedido?.id || ''),
    idPedido: String(pedido?.idPedido || ''),
    cliente: String(pedido?.cliente || 'Cliente não informado'),
    telefone: String(pedido?.telefone || ''),
    endereco: pedido?.endereco || { rua: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' },
    itens: Array.isArray(pedido?.itens) ? pedido.itens : [],
    valorItens: Number(pedido?.valorItens || 0),
    valorBrutoItens: Number((pedido as any)?.valorBrutoItens ?? pedido?.valorItens ?? 0),
    descontoCombos: Number((pedido as any)?.descontoCombos ?? 0),
    taxaEntrega: Number(pedido?.taxaEntrega || 0),
    valorTotal: Number(pedido?.valorTotal || 0),
    status: (pedido?.status as StatusPedido) || 'RECEBIDO',
    dataCriacao: String(pedido?.dataCriacao || new Date().toISOString()),
    dataAtualizacao: String(pedido?.dataAtualizacao || new Date().toISOString()),
    observacao: String(pedido?.observacao || '')
  };
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `pedido-${safePedido.id}`,
    data: {
      type: 'pedido',
      pedido: safePedido,
    },
  });

  const style: React.CSSProperties = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
    marginBottom: 16, // 16px = 2 * theme.spacing(1)
  };

  const formatCurrency = (value: number) => {
    try {
      if (typeof value !== 'number' || isNaN(value)) {
        return 'R$ 0,00';
      }
      const formatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
      return typeof formatted === 'string' ? formatted : 'R$ 0,00';
    } catch (error) {
      console.error('Erro ao formatar moeda:', error);
      return 'R$ 0,00';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString || typeof dateString !== 'string') {
        return 'Data inválida';
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      const formatted = format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
      return typeof formatted === 'string' ? formatted : 'Data inválida';
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        '&:hover': {
          '& .MuiCard-root': {
            boxShadow: 3,
          },
        },
      }}
    >
      <Card 
        variant="outlined"
        sx={{
          '&:hover': {
            cursor: 'pointer',
          },
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" component="div" fontWeight="bold">
              #{safePedido.idPedido}
            </Typography>
            <Chip 
              label={safePedido.status.replace('_', ' ')}
              color={getStatusColor(safePedido.status) as any}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" mb={1}>
            {safePedido.cliente || 'Cliente não informado'}
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {safePedido.itens?.length || 0} ite{(safePedido.itens?.length || 0) !== 1 ? 'ns' : 'm'}
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold">
              {formatCurrency(safePedido.valorTotal || 0)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.disabled" display="block" mt={1}>
            {formatDate(safePedido.dataCriacao || '')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
